/**
 * CustomSelect — reusable custom select web component.
 *
 * Usage:
 *   <custom-select data-label="Choose a drink" data-placeholder="Select one...">
 *     <select name="drink" aria-label="Drink">
 *       <optgroup label="Hot">
 *         <option value="latte" data-image="latte.png">Latte</option>
 *         <option value="americano" data-image="americano.png">Americano</option>
 *       </optgroup>
 *       <optgroup label="Cold">
 *         <option value="cold-brew" data-image-src="https://cdn.example.com/cold-brew.png">Cold Brew</option>
 *       </optgroup>
 *     </select>
 *   </custom-select>
 *
 * Attributes on <custom-select>:
 *   data-label        — fallback label shown in the mobile panel header when no option is selected.
 *   data-placeholder  — placeholder text shown in the trigger before any selection.
 *
 * Attributes on the inner <select>:
 *   data-mobile-header-title — overrides the mobile panel header title. Can be set dynamically
 *                              (e.g. to reflect the current drink category). HTML tags are stripped
 *                              before display; only the plain-text content is rendered.
 *
 * Attributes on each <option>:
 *   data-image      — filename of an image in the theme's assets folder (e.g. "latte.png").
 *                     The component resolves the full URL automatically using the asset base URL.
 *   data-image-src  — fully-qualified URL to an image (e.g. a CDN or Shopify file URL).
 *                     Used as-is with no base URL prepended.
 *   Only one of data-image or data-image-src should be set per option; if both are present,
 *   data-image takes precedence. Options without either attribute render text only.
 *
 * The native <select> is hidden but stays in the DOM to drive form submission.
 * Options can be flat or grouped via <optgroup> elements.
 *
 * Desktop: renders as a dropdown panel anchored below the trigger.
 * Mobile (<768px): renders as a fixed bottom-sheet with a sticky header and a scrollable body.
 *   Opening saves window.scrollY and applies position:fixed to <body> to lock background scroll
 *   on iOS Safari; closing restores the scroll position before releasing the lock.
 *
 * ARIA: combobox/listbox pattern with aria-activedescendant for keyboard navigation.
 * Keyboard: ArrowUp/Down, Home, End, Enter/Space to select.
 *           Escape — closes and returns focus to the trigger.
 *           Tab    — closes without stealing focus, allowing normal tab flow to continue.
 *
 * CSS hooks:
 *   .custom-select--open              on host when open
 *   .custom-select__backdrop--visible on backdrop when open (mobile only)
 *   .body--custom-select-open         on <body> to lock scroll on mobile
 *   .custom-select__panel-header      sticky header row inside the mobile panel
 *   .custom-select__panel-title       title span inside the mobile panel header
 *   .custom-select__close-btn         close button inside the mobile panel header
 *   .custom-select__panel-body        scrollable options container inside the mobile panel
 *   .custom-select__option--selected  on the selected option
 *   .custom-select__option--focused   on the keyboard-focused option
 *   .custom-select__option--disabled  on disabled options
 *   .custom-select__option-image      image inside an option row
 *   .custom-select__selected-image    image shown in the trigger for the selected option
 */
class CustomSelect extends HTMLElement {
  connectedCallback() {
    this.nativeSelect = this.querySelector('select');
    if (!this.nativeSelect) return;
    this._mq = window.matchMedia('(max-width: 767px)');
    const scriptSrc = document.querySelector('script[src*="custom-select"]')?.src ?? '';
    this._assetBaseUrl = scriptSrc.slice(0, scriptSrc.lastIndexOf('/') + 1);
    this._buildUI();
    this._bindEvents();
    this._syncFromNative();

    this._nativeChangeHandler = () => this._syncFromNative();
    this.nativeSelect.addEventListener('change', this._nativeChangeHandler);

    this._observer = new MutationObserver(() => {
      this._buildOptionList();
      this._syncFromNative();
    });
    this._observer.observe(this.nativeSelect, { childList: true });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._handleDocumentClick);
    document.removeEventListener('keydown', this._handleKeydown);
    if (this._backdrop) this._backdrop.remove();
    if (this._mobilePanel) this._mobilePanel.remove();
    if (this._observer) this._observer.disconnect();
    if (this._nativeChangeHandler) {
      this.nativeSelect?.removeEventListener('change', this._nativeChangeHandler);
    }
  }

  // ---------------------------------------------------------------------------
  // Build
  // ---------------------------------------------------------------------------

  _buildUI() {
    const baseId = this.nativeSelect.id || `custom-select-${Math.random().toString(36).slice(2, 7)}`;
    const panelId = `${baseId}-panel`;
    const mobilePanelId = `${baseId}-mobile-panel`;

    // Keep native select accessible to form but hidden from view
    this.nativeSelect.setAttribute('aria-hidden', 'true');
    this.nativeSelect.setAttribute('tabindex', '-1');
    this.nativeSelect.style.display = 'none';

    // Trigger button — acts as the visible "select box"
    this._trigger = document.createElement('button');
    this._trigger.type = 'button';
    this._trigger.className = 'custom-select__trigger';
    this._trigger.setAttribute('role', 'combobox');
    this._trigger.setAttribute('aria-haspopup', 'listbox');
    this._trigger.setAttribute('aria-expanded', 'false');
    this._trigger.setAttribute('aria-controls', panelId);
    const selectedImage = document.createElement('img');
    selectedImage.className = 'custom-select__selected-image';
    selectedImage.src = '';
    selectedImage.alt = '';
    selectedImage.setAttribute('aria-hidden', 'true');
    selectedImage.hidden = true;
    const value = document.createElement('span');
    value.className = 'custom-select__value';
    value.textContent = this.dataset.placeholder || '';
    const arrow = document.createElement('span');
    arrow.className = 'custom-select__arrow';
    arrow.setAttribute('aria-hidden', 'true');
    this._trigger.append(selectedImage, value, arrow);

    // Desktop panel — dropdown anchored below the trigger
    this._panel = document.createElement('div');
    this._panel.id = panelId;
    this._panel.className = 'custom-select__panel';
    this._panel.setAttribute('role', 'listbox');
    this._panel.hidden = true;

    // Mobile panel — bottom-sheet appended directly to <body>
    this._mobilePanel = document.createElement('div');
    this._mobilePanel.id = mobilePanelId;
    this._mobilePanel.className = `custom-select__panel custom-select__panel--mobile${this.nativeSelect.dataset.reserveTheme === 'true' ? ' custom-select__dark' : ''}${this.dataset.greyTheme ? ' custom-select__grey' : ''}`;
    this._mobilePanel.setAttribute('role', 'listbox');
    this._mobilePanel.hidden = true;

    const panelLabel = this.dataset.label || this.nativeSelect.getAttribute('aria-label') || '';
    this._closeBtn = document.createElement('button');
    this._closeBtn.type = 'button';
    this._closeBtn.className = 'custom-select__close-btn';
    this._closeBtn.setAttribute('aria-label', 'Close');
    // prettier-ignore
    this._closeBtn.innerHTML = '<span aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M15.5867 4.41205C15.9121 4.73748 15.9121 5.26512 15.5867 5.59056L5.58665 15.5906C5.26121 15.916 4.73358 15.916 4.40814 15.5906C4.0827 15.2651 4.0827 14.7375 4.40814 14.412L14.4081 4.41205C14.7336 4.08661 15.2612 4.08661 15.5867 4.41205Z" fill="currentColor"/><path fill-rule="evenodd" clip-rule="evenodd" d="M4.40814 4.41205C4.73358 4.08661 5.26121 4.08661 5.58665 4.41205L15.5867 14.412C15.9121 14.7375 15.9121 15.2651 15.5867 15.5906C15.2612 15.916 14.7336 15.916 14.4081 15.5906L4.40814 5.59056C4.0827 5.26512 4.0827 4.73748 4.40814 4.41205Z" fill="currentColor"/></svg></span>';
    this._panelTitle = document.createElement('span');
    this._panelTitle.className = 'custom-select__panel-title';
    this._panelTitle.textContent = (this.nativeSelect.dataset.mobileHeaderTitle || panelLabel).replace(/<[^>]*>/g, '');
    const mobileHeader = document.createElement('div');
    mobileHeader.className = 'custom-select__panel-header';
    mobileHeader.append(this._panelTitle, this._closeBtn);
    this._mobilePanel.appendChild(mobileHeader);

    this._mobilePanelBody = document.createElement('div');
    this._mobilePanelBody.className = 'custom-select__panel-body';
    this._mobilePanel.appendChild(this._mobilePanelBody);

    // Backdrop — covers the page on mobile, clicking it closes the panel
    this._backdrop = document.createElement('div');
    this._backdrop.className = 'custom-select__backdrop';
    this._backdrop.setAttribute('aria-hidden', 'true');

    this._options = [];
    this._mobileOptions = [];
    this._buildOptionList();

    this.append(this._trigger, this._panel);
    document.body.append(this._mobilePanel, this._backdrop);
  }

  _buildOptionList() {
    this._options = [];
    this._mobileOptions = [];
    this._panel.querySelectorAll('.custom-select__group, .custom-select__option').forEach((el) => el.remove());
    this._mobilePanel.querySelectorAll('.custom-select__group, .custom-select__option').forEach((el) => el.remove());

    const groups = this.nativeSelect.querySelectorAll('optgroup');

    if (groups.length) {
      groups.forEach((group) => {
        this._panel.appendChild(this._createGroupEl(group, this._panel.id, this._options));
        this._mobilePanelBody.appendChild(this._createGroupEl(group, this._mobilePanel.id, this._mobileOptions));
      });
    } else {
      this.nativeSelect.querySelectorAll('option').forEach((opt) => {
        if (!opt.value) return;
        this._panel.appendChild(this._createOptionEl(opt, this._panel.id, this._options));
        this._mobilePanelBody.appendChild(this._createOptionEl(opt, this._mobilePanel.id, this._mobileOptions));
      });
    }
  }

  _createGroupEl(group, panelId, optionsArray) {
    const groupEl = document.createElement('div');
    groupEl.className = 'custom-select__group';
    groupEl.setAttribute('role', 'group');
    groupEl.setAttribute('aria-label', group.label);
    const heading = document.createElement('div');
    heading.className = 'custom-select__group-label';
    heading.setAttribute('aria-hidden', 'true');
    heading.textContent = group.label;
    groupEl.appendChild(heading);
    group
      .querySelectorAll('option')
      .forEach((opt) => groupEl.appendChild(this._createOptionEl(opt, panelId, optionsArray)));
    return groupEl;
  }

  _createOptionEl(option, panelId, optionsArray) {
    const index = optionsArray.length;
    const el = document.createElement('div');
    el.className = 'custom-select__option';
    el.setAttribute('role', 'option');
    el.setAttribute('aria-selected', 'false');
    el.dataset.value = option.value;
    el.dataset.index = String(index);
    el.id = `${panelId}-opt-${index}`;
    el.dataset.label = option.textContent.trim();
    if (option.dataset.image || option.dataset.imageSrc) {
      if (option.dataset.image) el.dataset.image = option.dataset.image;
      if (option.dataset.imageSrc) el.dataset.imageSrc = option.dataset.imageSrc;
      const img = document.createElement('img');
      img.className = 'custom-select__option-image';
      img.src = option.dataset.image ? `${this._assetBaseUrl}${option.dataset.image}` : option.dataset.imageSrc;
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');
      el.appendChild(img);
    }
    const text = document.createElement('span');
    text.className = 'custom-select__option-text';
    text.textContent = option.textContent.trim();
    el.appendChild(text);
    if (option.disabled) {
      el.setAttribute('aria-disabled', 'true');
      el.classList.add('custom-select__option--disabled');
    }
    optionsArray.push(el);
    return el;
  }

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  _syncFromNative() {
    const selected = this.nativeSelect.options[this.nativeSelect.selectedIndex];
    if (selected && selected.value) {
      const customOpt = this._options.find((o) => o.dataset.value === selected.value);
      const image = customOpt?.dataset.image
        ? `${this._assetBaseUrl}${customOpt.dataset.image}`
        : customOpt?.dataset.imageSrc;
      this._setDisplay(selected.value, selected.textContent.trim(), image);
    }
  }

  _setDisplay(value, label, imageUrl) {
    this._trigger.querySelector('.custom-select__value').textContent = label;
    if (this._panelTitle) {
      const fallback = this.dataset.label || this.nativeSelect.getAttribute('aria-label') || '';
      this._panelTitle.textContent = (this.nativeSelect.dataset.mobileHeaderTitle || fallback).replace(/<[^>]*>/g, '');
    }
    const triggerImg = this._trigger.querySelector('.custom-select__selected-image');
    if (triggerImg) {
      if (imageUrl) {
        triggerImg.src = imageUrl;
        triggerImg.hidden = false;
      } else {
        triggerImg.hidden = true;
      }
    }
    // Sync selected state in both desktop and mobile option lists
    [...this._options, ...this._mobileOptions].forEach((opt) => {
      const isSelected = opt.dataset.value === value;
      opt.setAttribute('aria-selected', String(isSelected));
      opt.classList.toggle('custom-select__option--selected', isSelected);
      if (isSelected) this._activeIndex = parseInt(opt.dataset.index, 10);
    });
  }

  _pickOption(value, label, imageUrl) {
    this.nativeSelect.value = value;
    this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    this._setDisplay(value, label, imageUrl);
    this.close();
  }

  // ---------------------------------------------------------------------------
  // Open / close
  // ---------------------------------------------------------------------------

  open() {
    CustomSelect.closeAll(this);
    this._isOpen = true;
    this._trigger.setAttribute('aria-expanded', 'true');
    this.classList.add('custom-select--open');

    if (this._mq.matches) {
      // Mobile: show body-level bottom-sheet
      this._mobilePanel.hidden = false;
      this._trigger.setAttribute('aria-controls', this._mobilePanel.id);
      this._backdrop.classList.add('custom-select__backdrop--visible');
      this._scrollY = window.scrollY;
      document.body.style.top = `-${this._scrollY}px`;
      document.body.classList.add('body--custom-select-open');
      this._activeOptions = this._mobileOptions;
    } else {
      // Desktop: show inline dropdown
      this._panel.hidden = false;
      this._trigger.setAttribute('aria-controls', this._panel.id);
      this._activeOptions = this._options;
    }

    // Focus the currently selected option, falling back to the first
    const focused =
      this._activeOptions.find((o) => o.getAttribute('aria-selected') === 'true') || this._activeOptions[0];
    if (focused) {
      this._setFocused(focused);
      focused.scrollIntoView({ block: 'nearest' });
    }
  }

  close(refocus = true) {
    if (!this._isOpen) return;
    this._isOpen = false;
    this._panel.hidden = true;
    this._mobilePanel.hidden = true;
    this._trigger.setAttribute('aria-expanded', 'false');
    this._trigger.removeAttribute('aria-activedescendant');
    this.classList.remove('custom-select--open');
    this._backdrop.classList.remove('custom-select__backdrop--visible');
    document.body.classList.remove('body--custom-select-open');
    if (this._scrollY !== undefined) {
      document.body.style.top = '';
      window.scrollTo({ top: this._scrollY, behavior: 'instant' });
      this._scrollY = undefined;
    }
    if (refocus) this._trigger.focus();
  }

  _setFocused(el) {
    [...this._options, ...this._mobileOptions].forEach((o) => o.classList.remove('custom-select__option--focused'));
    el.classList.add('custom-select__option--focused');
    this._trigger.setAttribute('aria-activedescendant', el.id);
    this._activeIndex = parseInt(el.dataset.index, 10);
  }

  // ---------------------------------------------------------------------------
  // Events
  // ---------------------------------------------------------------------------

  _bindEvents() {
    // Toggle open/close on trigger click
    this._trigger.addEventListener('click', () => {
      this._isOpen ? this.close() : this.open();
    });

    // Open with arrow keys or Enter/Space when trigger is focused
    this._trigger.addEventListener('keydown', (e) => {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key) && !this._isOpen) {
        e.preventDefault();
        this.open();
      }
    });

    // Select an option on click — bound on both desktop and mobile panels
    const handleOptionClick = (e) => {
      const opt = e.target.closest('.custom-select__option');
      if (!opt || opt.getAttribute('aria-disabled') === 'true') return;
      const image = opt.dataset.image ? `${this._assetBaseUrl}${opt.dataset.image}` : opt.dataset.imageSrc;
      this._pickOption(opt.dataset.value, opt.dataset.label, image);
    };
    this._panel.addEventListener('click', handleOptionClick);
    this._mobilePanel.addEventListener('click', handleOptionClick);

    // Mobile close button and backdrop
    this._closeBtn.addEventListener('click', () => this.close());
    this._backdrop.addEventListener('click', () => this.close());

    // Close when clicking outside (exclude the body-level mobile panel)
    this._handleDocumentClick = (e) => {
      if (
        this._isOpen &&
        !this.contains(e.target) &&
        !this._mobilePanel.contains(e.target) &&
        e.target !== this._backdrop
      ) {
        this.close();
      }
    };

    // Full keyboard navigation within the open panel
    this._handleKeydown = (e) => {
      if (!this._isOpen) return;

      const enabled = (this._activeOptions || this._options).filter((o) => o.getAttribute('aria-disabled') !== 'true');
      const pos = enabled.findIndex((o) => parseInt(o.dataset.index, 10) === this._activeIndex);

      switch (e.key) {
        case 'Escape':
          this.close();
          break;
        case 'Tab':
          this.close(false);
          break;
        case 'ArrowDown': {
          e.preventDefault();
          const next = enabled[Math.min(pos + 1, enabled.length - 1)];
          this._setFocused(next);
          next.scrollIntoView({ block: 'nearest' });
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const prev = enabled[Math.max(pos - 1, 0)];
          this._setFocused(prev);
          prev.scrollIntoView({ block: 'nearest' });
          break;
        }
        case 'Home': {
          e.preventDefault();
          this._setFocused(enabled[0]);
          enabled[0].scrollIntoView({ block: 'nearest' });
          break;
        }
        case 'End': {
          e.preventDefault();
          const last = enabled[enabled.length - 1];
          this._setFocused(last);
          last.scrollIntoView({ block: 'nearest' });
          break;
        }
        case 'Enter':
        case ' ': {
          e.preventDefault();
          const active = enabled.find((o) => parseInt(o.dataset.index, 10) === this._activeIndex);
          const image = active?.dataset.image
            ? `${this._assetBaseUrl}${active.dataset.image}`
            : active?.dataset.imageSrc;
          if (active) this._pickOption(active.dataset.value, active.dataset.label, image);
          break;
        }
        default:
          break;
      }
    };

    document.addEventListener('click', this._handleDocumentClick);
    document.addEventListener('keydown', this._handleKeydown);
  }

  // ---------------------------------------------------------------------------
  // Static helpers
  // ---------------------------------------------------------------------------

  /**
   * Close all open CustomSelect instances except the one passed in.
   * Called automatically on open() so only one is ever open at a time.
   */
  static closeAll(except = null) {
    document.querySelectorAll('custom-select').forEach((el) => {
      if (el !== except) el.close();
    });
  }
}

customElements.define('custom-select', CustomSelect);
